/**
 * CustomBadge Component
 *
 * A reusable badge component with configurable color variants and automatic dark mode support.
 * Provides minimal, modern styling with consistent appearance across light and dark themes.
 *
 * Features:
 * - Type-safe variant system with compile-time validation
 * - Automatic dark mode color switching
 * - Easily extensible for new color variants
 * - Consistent padding and border styling
 *
 * @example
 * // Blue badge for non-zero counts
 * <CustomBadge variant="blue">5 RPNs</CustomBadge>
 *
 * @example
 * // Grey badge for zero counts or neutral states
 * <CustomBadge variant="grey">0 RPNs</CustomBadge>
 *
 * @example
 * // Grey badge for attribute values
 * <CustomBadge variant="grey">US</CustomBadge>
 *
 * @example
 * // Red badge for removed/old values
 * <CustomBadge variant="red">Old Value</CustomBadge>
 *
 * @example
 * // Green badge for new/added values
 * <CustomBadge variant="green">New Value</CustomBadge>
 */
import { Badge } from '@amzn/awsui-components-core';
import { Mode } from '@amzn/awsui-global-styles';
import { useTheme } from 'src/contexts/ThemeContext';

interface BadgeColorScheme {
  /** Background color for the badge */
  background: string;
  /** Text color for the badge content */
  color: string;
  /** Border color for the badge outline */
  borderColor: string;
}

interface BadgeVariantConfig {
  /** Color scheme for light mode */
  light: BadgeColorScheme;
  /** Color scheme for dark mode */
  dark: BadgeColorScheme;
}

const BADGE_VARIANTS = {
  blue: {
    light: {
      background: 'rgb(219, 234, 254)',
      color: 'rgb(30, 64, 175)',
      borderColor: 'rgb(147, 197, 253)',
    },
    dark: {
      background: 'rgb(37, 99, 235)',
      color: 'rgb(219, 234, 254)',
      borderColor: 'rgb(59, 130, 246)',
    },
  },
  grey: {
    light: {
      background: 'rgb(229, 231, 235)',
      color: 'rgb(17, 24, 39)',
      borderColor: 'rgb(209, 213, 219)',
    },
    dark: {
      background: 'rgb(75, 85, 99)',
      color: 'rgb(243, 244, 246)',
      borderColor: 'rgb(107, 114, 128)',
    },
  },
  red: {
    light: {
      background: 'rgb(254, 226, 226)',
      color: 'rgb(153, 27, 27)',
      borderColor: 'rgb(252, 165, 165)',
    },
    dark: {
      background: 'rgb(127, 29, 29)',
      color: 'rgb(254, 202, 202)',
      borderColor: 'rgb(185, 28, 28)',
    },
  },
  green: {
    light: {
      background: 'rgb(220, 252, 231)',
      color: 'rgb(22, 101, 52)',
      borderColor: 'rgb(134, 239, 172)',
    },
    dark: {
      background: 'rgb(20, 83, 45)',
      color: 'rgb(187, 247, 208)',
      borderColor: 'rgb(22, 163, 74)',
    },
  },
} as const satisfies Record<string, BadgeVariantConfig>;

type BadgeVariant = keyof typeof BADGE_VARIANTS;

interface CustomBadgeProps {
  /** Content to display inside the badge */
  children: React.ReactNode;
  /** Color variant to apply (determines styling) */
  variant: BadgeVariant;
  /** Border width in pixels or CSS unit (default: '1px') */
  borderWidth?: string;
  /** Horizontal padding in pixels or CSS unit (default: '8px') */
  paddingInline?: string;
}

export const CustomBadge = ({
  children,
  variant,
  borderWidth = '1px',
  paddingInline = '8px',
}: CustomBadgeProps): JSX.Element => {
  const { mode } = useTheme();
  const isDarkMode = mode === Mode.Dark;
  const styles = BADGE_VARIANTS[variant][isDarkMode ? 'dark' : 'light'];

  return (
    <Badge
      style={{
        root: {
          background: styles.background,
          color: styles.color,
          borderColor: styles.borderColor,
          borderWidth,
          paddingInline,
        },
      }}
    >
      {children}
    </Badge>
  );
};
